(ns storrito.upload.story-upload
  (:require [storrito.video :as video]
            [storrito.instagram.story.editor.quick-stories :refer [save-image-quick-story!
                                                                   save-video-quick-story!]]
            [storrito.image :as image]
            [app.upload-manager.core :as upload-manager]
            [clojure.string :as str]
            [storrito.video.config :as vc]
            [storrito.instagram.story.editor.render :as render]
            [sv.core.async :refer-macros [<?]]
            [ui.editor2.media-limits :as media-limits])
  (:require-macros [cljs.core.async.macros :refer [go]]))

;;; 100 files in one drop
(def +file-count-limit+ 100)

;;; 512Mb
(def +video-file-size-limit+ (* 1024 1024 512))

;;; 16:9 images with this width can be scaled to cover the entire 1080x1920 story.
;; 1920/1080 = 1.778, 1.778*1920 = 3414
(def +max-image-side-length+ 3500)


(defn file-type [file]
  (let [mime file.type]
    (cond
      (str/starts-with? mime "image/") :image
      (str/starts-with? mime "video/") :video)))

(defn rejection
  "Why the quick upload rejects a file, for the upload status
   (`ui.upload-status`): the `:file-name` (nil when the whole batch is
   rejected), the `:reason` and a `:hint` how to fix it."
  [file-name reason hint]
  {:file-name file-name
   :reason reason
   :hint hint})

(defn megabytes
  "The `bytes` as megabytes for the user, rounded up to one decimal, so a
   file just above a limit never shows the limit itself (20, 20.1, 34)."
  [bytes]
  (str (/ (js/Math.ceil (* 10 (/ bytes 1024 1024)))
          10)))

(defn seconds
  "The `duration` in seconds for the user, rounded down to one decimal,
   so a video just below a minimum never shows the minimum itself."
  [duration]
  (str (/ (js/Math.floor (* 10 duration))
          10)))

(defn too-many-files
  [file-count]
  (rejection nil
             (str file-count " files picked at once.")
             (str "Upload at most " +file-count-limit+ " files at a time.")))

(defn unsupported-type
  [file-name mime-type]
  (rejection file-name
             (str "Not a supported file type"
                  (when (seq mime-type)
                    (str " (" mime-type ")"))
                  ".")
             "Use JPG, PNG, MP4 or MOV."))

(defn ^:private validate-video
  "Checks the size and (when the metadata has it) the duration of a
   video file. Returns nil or the `rejection`."
  [file metadata]
  (let [file-name (.-name file)
        size (.-size file)
        duration (::video/duration metadata)]
    (cond
      (nil? size)
      (rejection file-name
                 "The file size can't be read."
                 "Pick the file again, or save it as a new MP4.")

      (> size +video-file-size-limit+)
      (rejection file-name
                 (str "Larger than " (megabytes +video-file-size-limit+)
                      " MB (" (megabytes size) " MB).")
                 "Compress or shorten the video.")

      (and duration
           (< duration vc/ig-story-min-duration))
      (rejection file-name
                 (str "Only " (seconds duration) " s long.")
                 (str "Stories need videos of at least "
                      vc/ig-story-min-duration " s.")))))

(defn ^:private validate-image-file
  "Validates an image file for size and dimensions. Returns a channel
   that will contain nil if valid, or the `rejection`."
  [file]
  (go
    (let [file-name (.-name file)
          file-size (.-size file)]
      (if (> file-size media-limits/max-image-file-size-bytes)
        (rejection file-name
                   (str "Larger than " (megabytes media-limits/max-image-file-size-bytes)
                        " MB (" (megabytes file-size) " MB).")
                   "Save it as a JPG or scale it down.")
        (try
          (let [img (<? (image/load-image file))
                width (.-width img)
                height (.-height img)
                limit media-limits/max-image-dimension-pixels]
            (when (or (> width limit)
                      (> height limit))
              (rejection file-name
                         (str width " × " height " px is larger than "
                              limit " × " limit " px.")
                         (str "Scale it down to at most " limit " px per side."))))
          (catch js/Error _
            (rejection file-name
                       "Can't be opened, the file may be damaged."
                       "Export it again as JPG or PNG.")))))))

(defn report-error!
  "Passes the `:message` for the user and the `:error` of a failed quick
   upload to the `:on-error` fn of the caller, which shows the message.
   The error itself (with its stack) is logged by
   `handle-quick-story-files!`."
  [{:keys [on-error error]}]
  (when on-error
    (on-error {:message (str "Could not create story! " (ex-message error))
               :error error})))

(defn report-checked!
  "Passes the result of the check to the `:on-checked` fn of the caller,
   once all files are validated and before the uploads start:
   `:accepted` is the number of files that get uploaded, `:rejected` the
   `rejection`s of the others."
  [{:keys [on-checked]} accepted rejected]
  (when (seq rejected)
    (js/console.warn "Rejected upload files:" (clj->js rejected)))
  (when on-checked
    (on-checked {:accepted accepted
                 :rejected rejected})))

(defn handle-quick-story-files!
  "Validates the `files`, uploads the valid ones and saves a story media
   per file. Rejected files do not stop the others, except when there
   are more than `+file-count-limit+` files, then none is uploaded.

   Returns a channel which yields the vector of the save responses (empty
   when no file was accepted), or the error when an upload failed. By
   then the caller has been told about rejected files and errors (see
   `report-checked!` and `report-error!`).

   The optional `params` are added to each `:story-upload/save!`
   command. The gallery (`ui.gallery`) passes its `:story-media/folder`
   this way.

   The optional `opts` may contain an `:on-checked` and an `:on-error`
   fn."
  ([files]
   (handle-quick-story-files! files nil))
  ([files params]
   (handle-quick-story-files! files params nil))
  ([files params opts]
   (go
     (try
       (if (> (count files) +file-count-limit+)
         (do
           (report-checked! opts 0 [(too-many-files (count files))])
           [])
         (let [images (volatile! [])
               videos (volatile! [])
               rejected (volatile! [])]
           (doseq [file files]
             (case (file-type file)
               :image (if-let [image-rejection (<? (validate-image-file file))]
                        (vswap! rejected conj image-rejection)
                        (if-let [constrained-image (<? (render/constrain-image-file! file
                                                                                     {:quality 0.9
                                                                                      :max-side-length +max-image-side-length+}))]
                          (vswap! images conj constrained-image)
                          (vswap! rejected conj
                                  (rejection (.-name file)
                                             "Couldn't be resized."
                                             "Try a smaller image."))))
               :video (let [metadata (<? (video/metadata file))]
                        (if-let [video-rejection (validate-video file metadata)]
                          (vswap! rejected conj video-rejection)
                          (vswap! videos conj file)))
               (vswap! rejected conj (unsupported-type (.-name file)
                                                       (.-type file)))))
           (report-checked! opts
                            (+ (count @images) (count @videos))
                            @rejected)
           ;; The files are uploaded and saved one after the other;
           ;; the result is the vector of the save responses:
           (loop [[[entity-type blob] & more] (concat (map vector (repeat :type.instagram.story/image) @images)
                                                      (map vector (repeat :type.instagram/video) @videos))
                  saved []]
             (if-not blob
               saved
               (let [blob-uuid (<? (upload-manager/upload-blob! {:blob blob
                                                                 :name (.-name blob)}))
                     save-fn (case entity-type
                               :type.instagram.story/image save-image-quick-story!
                               :type.instagram/video save-video-quick-story!)
                     response (<? (save-fn blob-uuid
                                           blob
                                           params))]
                 (recur more
                        (conj saved response)))))))
       (catch js/Error e
         (report-error! (assoc opts
                               :error e))
         ;; The user has seen the message via `report-error!`. A rethrow
         ;; here only escaped the go block as an uncaught error and
         ;; reached Sentry (STORRITO-V2-C2, STORRITO-V2-JV), where it
         ;; lumped server errors, lost connections and corrupt files
         ;; into one issue. The server logs its own 500s with an error
         ;; UUID. The returned channel carries the error instead.
         (js/console.error "Failed to create quick stories:" e)
         (ex-info (str "Failed to create quick stories: " (ex-message e))
                  {}
                  e))))))
